import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
    openDrawer,
    setCurrentDrawerId,
    setCurrentEntity,
    setCurrentOperation,
    setCurrentTenantId,
} from 'common/components/EditDrawer/editDrawerSlice';
import { UserProfileEntityContainer } from 'common/components/UserProfileEntityContainer/UserProfileEntityContainer';
import { UserProfilesDataGrid } from 'common/components/UserProfilesDataGrid/UserProfilesDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserProfileUserGroups } from 'common/services/api/UserProfileUserGroups/UserProfileUserGroups.thunk';
import {
    ActionDefinition,
    ColumnDefinition,
    ColumnType,
    DrawerEntitiesNames,
    Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { UserGroupUserMembershipCreateContent } from 'features/userGroupUserMembership/UserGroupUserMembershipCreateDrawerContent';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';
import { useSnackbar } from 'notistack';

import { UserProfileUserGroupDeleteDialog } from './UserProfileUserGroupDeleteDialog';
import {
    selectProcessing,
    selectUserProfileUserGroupsByTenantId,
} from './userProfileUserGroupsSlice';

const CONTAINER_INLINE_STYLES = {
    backgroundColor: 'secondary.light',
    borderRadius: '4px',
    border: 1,
    borderColor: 'info.dark',
    height: 'max-content',
    marginBottom: '1rem',
    width: 'calc(50% - 12px)'
};

type UserProfileUserGroupsListProps = {
    tenant: IUserProfileTenant;
}

export function UserProfileUserGroups({ tenant }: Readonly<UserProfileUserGroupsListProps>) {

    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const userProfileUserGroups = useAppSelector(selectUserProfileUserGroupsByTenantId(tenant.id));
    const processing = useAppSelector(selectProcessing);

    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.AppManager, Roles.MultiTenantRO ]);
    const shouldDisableLinkButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const [sortModel, setSortModel] = usePalmSortModel();

    useEffect(() => {
        if (userProfileUserGroups) {
            return;
        }
        const fetchUserProfileUserGroupsParams = {
            tenantId: tenant.id,
            userId: params?.userId ?? ''
        };
        dispatch(fetchUserProfileUserGroups(fetchUserProfileUserGroupsParams))
            .unwrap()
            .catch((error) => {
                handleListFetchError(extractStatusCode(error.message), 'User Profile User Groups', enqueueSnackbar);
            });
    }, []);

    const handleCreateButtonClick = () => {
        dispatch(setCurrentOperation(Operations.Link));
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserProfileUserGroup));
        dispatch(setCurrentTenantId(tenant.id));
        dispatch(setCurrentDrawerId(DrawerIDs.UserGroup));
        dispatch(openDrawer());
    };

    const handleOpenLink = useCallback((userGroupId: string) => {
        return `/${Routes.ApplicationSuites}/${tenant.applicationSuiteId}/${Routes.Tenants}/${tenant.id}/${Routes.UserGroups}/${userGroupId}/${Routes.Users}`;
    }, []);

    const handleOpenButtonClick = useCallback((userGroupId: string) => {
        navigate(handleOpenLink(userGroupId));
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: handleOpenLink },
        { action: Operations.Delete, disabled: shouldDisableLinkButton },
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'name', columnName: 'User Group Name', columnType: ColumnType.First, copy: false },
        { columnId: 'description', columnName: 'User Group Description', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', size: 0.4, columnType: ColumnType.Action, sortable: false }
    ];

    return (
        <UserProfileEntityContainer
            title='User Groups'
            isCreateButtonDisabled={shouldDisableLinkButton}
            shouldShowCreateButton={true}
            containerInlineStyles={CONTAINER_INLINE_STYLES}
            createButtonHandler={handleCreateButtonClick}
        >

            <Box sx={{ margin: '0 24px 24px 24px' }}>
                <UserProfilesDataGrid
                    tenant={tenant}
                    rows={userProfileUserGroups}
                    actionDefinitions={actionDefinitions}
                    columnDefinitions={columnDefinitions}
                    processing={processing === tenant.id}
                    currentEntityType={DrawerEntitiesNames.UserProfileUserGroup}
                    title='User Groups'
                    customCreateEditTitle='User Group'
                    contentFunction={UserGroupUserMembershipCreateContent}
                    deleteFunction={UserProfileUserGroupDeleteDialog}
                    customDeleteTitle={'Are you sure you want to remove this User from this User Group?'}
                    rowClick={shouldDisableOpenButton ? undefined : handleOpenButtonClick}
                    rowLink={handleOpenLink}
                    sortModel={sortModel}
                    clientSortHandler={setSortModel}
                />
            </Box>
        </UserProfileEntityContainer>
    );
}
